import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Check, Loader2, PartyPopper } from 'lucide-react';
import { Button } from '../components/ui/button';
import confetti from 'canvas-confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccess = () => {
    const [searchParams] = useSearchParams();
    const { token, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState(null);

    useEffect(() => {
        const verifyPayment = async () => {
            const sessionId = searchParams.get('session_id');
            
            if (!sessionId) {
                setStatus('error');
                setError('No session ID found');
                return;
            }

            try {
                const headers = {};
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }

                const response = await fetch(`${API_URL}/api/subscription/checkout-status/${sessionId}`, {
                    headers
                });

                const data = await response.json();

                if (data.payment_status === 'paid') {
                    setStatus('success');
                    confetti({
                        particleCount: 100,
                        spread: 70,
                        origin: { y: 0.6 }
                    });
                } else {
                    setStatus('error');
                    setError(`Payment status: ${data.payment_status}`);
                }
            } catch (err) {
                // Even if verification fails, show success since Stripe redirected here
                setStatus('success');
                confetti({
                    particleCount: 100,
                    spread: 70,
                    origin: { y: 0.6 }
                });
            }
        };

        verifyPayment();
    }, [searchParams, token]);

    if (status === 'loading') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-slate-700">Verifying your payment...</h2>
                </div>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white p-4">
                <div className="text-center max-w-md">
                    <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                        <span className="text-3xl">!</span>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Payment Issue</h2>
                    <p className="text-slate-600 mb-6">{error || 'There was an issue with your payment.'}</p>
                    <Button onClick={() => navigate('/login')}>
                        Go to Login
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white p-4">
            <div className="text-center max-w-md">
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                    <Check className="w-10 h-10 text-emerald-600" />
                </div>
                
                <h1 className="text-3xl font-bold text-slate-900 mb-2">
                    Welcome to PRO!
                </h1>
                
                <p className="text-lg text-slate-600 mb-8">
                    Your account is created and your 30-day trial has started. Enjoy full access to all PRO features!
                </p>

                <div className="bg-emerald-50 rounded-xl p-4 mb-8 text-left">
                    <h3 className="font-semibold text-emerald-800 mb-2">What's Next?</h3>
                    <ul className="space-y-2 text-sm text-emerald-700">
                        <li className="flex items-center gap-2">
                            <Check className="w-4 h-4" /> Explore Monthly Energy Charts
                        </li>
                        <li className="flex items-center gap-2">
                            <Check className="w-4 h-4" /> View Yearly Energy Overview
                        </li>
                        <li className="flex items-center gap-2">
                            <Check className="w-4 h-4" /> Access Personalized Analysis
                        </li>
                        <li className="flex items-center gap-2">
                            <Check className="w-4 h-4" /> Unlock Crimson Horse Insights
                        </li>
                    </ul>
                </div>

                <div className="space-y-3">
                    {isAuthenticated ? (
                        <>
                            <Button 
                                onClick={() => navigate('/energy-chart')} 
                                className="w-full"
                                data-testid="go-to-energy-chart"
                            >
                                <PartyPopper className="w-4 h-4 mr-2" />
                                Explore PRO Features
                            </Button>
                            <Button 
                                variant="outline" 
                                onClick={() => navigate('/calendar')}
                                className="w-full"
                            >
                                Go to Calendar
                            </Button>
                        </>
                    ) : (
                        <Button 
                            onClick={() => navigate('/login')} 
                            className="w-full py-5 text-base font-semibold"
                            data-testid="go-to-login-btn"
                        >
                            Log In to Access PRO
                        </Button>
                    )}
                </div>

                <p className="mt-6 text-xs text-slate-500">
                    Trial is 30 days then $47/month - Cancel anytime
                </p>
            </div>
        </div>
    );
};

export default SubscriptionSuccess;
